import {
    isEhrPendingConfirmation,
    resolveShiftStatus,
    type UnifiedShiftStatus,
} from '@apps/web/components/dashboard/shift_list_filters';
import {
    CALENDAR_CHIP_PALETTE,
    formatAcceptedCaregiverNames,
    getShiftCalendarStatusBucket,
    isShiftFilledOutsideActiveOutreach,
    type ShiftOutreachStatus,
} from '@apps/web/components/shifts_calendar/calendar_model';
import { getRoleDisplayName } from '@apps/web/lib/caregiver_role_utils';
import {
    formatCompactTime,
    formatDateWithWeekday,
    formatDurationFromNow,
    formatTime,
} from '@apps/web/lib/date_utils';
import {
    getAcceptedCountForShift,
    getFilledShiftIds,
    getOutreachPausedAt,
    getOutreachRemovalReason,
    getOutreachResolvedAt,
    getScheduledOutreachStartAt,
    getScheduledOutreachState,
} from '@apps/web/lib/outreach_utils';
import {
    type JoinedOutreachRead,
    type Outreach,
    type Shift,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

export type ShiftCardTone =
    | 'critical'
    | 'warning'
    | 'active'
    | 'assign'
    | 'review'
    | 'clock'
    | 'info'
    | 'quiet';

export type ShiftCardKind =
    | 'missing'
    | 'cancelled'
    | 'completed'
    | 'in_progress'
    | 'clock_issue'
    | 'outreach_shift_filled'
    | 'outreach_filled_elsewhere'
    | 'outreach_discovery'
    | 'outreach_accepted'
    | 'outreach_needs_review'
    | 'outreach_paused'
    | 'outreach_scheduled'
    | 'outreach_working'
    | 'open'
    | 'scheduled'
    | 'pending_confirmation'
    | 'confirmed'
    | 'declined'
    | 'no_response'
    | 'needs_review_reply'
    | 'attention_external';

export type ShiftCardCta =
    | { readonly kind: 'find_caregivers' }
    | {
          readonly kind: 'view_outreach';
          readonly outreachId: string;
          readonly outreachAction?: 'assign_and_fill';
      }
    | { readonly kind: 'view_caregiver'; readonly caregiverId: string }
    | { readonly kind: 'view_conversation'; readonly conversationId: string }
    | { readonly kind: 'view_details' };

export type ShiftCardAction = {
    readonly label: string;
    readonly cta: ShiftCardCta;
};

// Right-aligned timing/meta slot in the strip header row: a filled urgency
// pill ("Starts in 1 day"), a tinted info pill ("Auto"), or plain muted text
// ("started 47m ago").
export type ShiftCardStripMeta =
    | { readonly kind: 'text'; readonly label: string }
    | {
          readonly kind: 'pill';
          readonly label: string;
          readonly style: 'urgent' | 'info';
      };

export type ShiftCardStrip = {
    readonly tone: ShiftCardTone;
    // Short bold coverage-status line ("No caregiver yet", "Reaching out").
    readonly title: string;
    readonly meta: ShiftCardStripMeta | null;
    readonly body: ReadonlyArray<string>;
    readonly showResponseSummary: boolean;
    // Highest-severity signal: red bordered sub-row shown when the shift
    // already started and is still uncovered/unconfirmed.
    readonly urgencyLine: string | null;
    readonly showAgentRunningIndicator: boolean;
};

export type ShiftCardUnreadBadge = {
    readonly count: number;
    readonly conversationId: string;
};

export type ShiftCardGroupSibling = {
    readonly shiftId: string;
    // "Thu, Jul 16 · 3p–7p" in the organization timezone.
    readonly label: string;
    readonly stateLabel: string;
    // Good-news states (filled, said yes) render green; the rest stay muted.
    readonly isPositive: boolean;
};

// How a grouped (multi-shift outreach) shift relates to its siblings. The
// title is always available from Outreach.shift_ids; sibling rows need the
// joined-outreach payload.
export type ShiftCardGroup = {
    // "Part of a 3-shift outreach, must be taken together".
    readonly title: string;
    // Sibling rows ordered by start time; null while joined-outreach data is
    // still loading.
    readonly siblings: ReadonlyArray<ShiftCardGroupSibling> | null;
    // Siblings not shown: beyond the display cap, or missing from the joined
    // payload.
    readonly hiddenSiblingCount: number;
};

export type ShiftCardState = {
    readonly kind: ShiftCardKind;
    readonly strip: ShiftCardStrip | null;
    readonly primaryAction: ShiftCardAction;
    readonly secondaryAction: ShiftCardAction | null;
    readonly ghostActions: ReadonlyArray<ShiftCardAction>;
    readonly unreadBadge: ShiftCardUnreadBadge | null;
    // Group relationship shown in the popover facts; null for shifts that
    // aren't part of a multi-shift outreach.
    readonly group: ShiftCardGroup | null;
};

type ShiftCardCore = {
    readonly kind: ShiftCardKind;
    readonly strip: ShiftCardStrip;
    readonly primaryAction: ShiftCardAction;
    readonly secondaryAction: ShiftCardAction | null;
};

const VIEW_DETAILS_CTA: ShiftCardCta = { kind: 'view_details' };

/**
 * Pure per-status situation-card model backing the calendar shift popover.
 * The popover is a renderer over this state; all status branching, copy, and
 * navigation-CTA selection live here so they can be tested directly.
 */
export function resolveShiftCardState({
    shift,
    outreachStatus,
    joinedOutreach,
    timezone,
    now,
    formatName = (name) => name ?? '',
    displayLastFirst = false,
}: {
    readonly shift: Shift;
    readonly outreachStatus: ShiftOutreachStatus | undefined;
    readonly joinedOutreach: JoinedOutreachRead | undefined;
    readonly timezone: string;
    readonly now: Date;
    readonly formatName?: (fullName: string | null | undefined) => string;
    readonly displayLastFirst?: boolean;
}): ShiftCardState {
    const unifiedStatus = resolveShiftStatus(shift.status, shift.latest_stage);
    const core = buildCardCore({
        shift,
        outreachStatus,
        joinedOutreach,
        timezone,
        now,
        unifiedStatus,
        formatName,
        displayLastFirst,
    });
    return finalizeCard(
        core,
        buildUnreadBadge(shift),
        buildCardGroup({ shift, outreachStatus, joinedOutreach, timezone }),
    );
}

const MAX_GROUP_SIBLINGS_SHOWN = 4;

/**
 * Group summary for a shift on a multi-shift fill outreach. Prefers the
 * joined per-client shift group (which knows whether the shifts must be
 * taken together) and falls back to the outreach's full shift list so the
 * group is still explained before the joined payload loads.
 */
function buildCardGroup({
    shift,
    outreachStatus,
    joinedOutreach,
    timezone,
}: {
    readonly shift: Shift;
    readonly outreachStatus: ShiftOutreachStatus | undefined;
    readonly joinedOutreach: JoinedOutreachRead | undefined;
    readonly timezone: string;
}): ShiftCardGroup | null {
    const outreach = outreachStatus?.activeFillOutreach;
    if (!outreach) {
        return null;
    }
    const joinedGroup = joinedOutreach?.shift_groups.find(
        (group) =>
            group.shift_ids.includes(shift.id) && group.shift_ids.length > 1,
    );
    const groupShiftIds = joinedGroup?.shift_ids ?? outreach.shift_ids;
    if (groupShiftIds.length < 2) {
        return null;
    }
    const mustBeTakenTogether = joinedGroup?.group_mode === 'all';
    const title = [
        `Part of a ${groupShiftIds.length}-shift outreach`,
        joinedGroup ? ' for this client' : '',
        mustBeTakenTogether ? ', must be taken together' : '',
    ].join('');
    const siblingIds = groupShiftIds.filter((id) => id !== shift.id);
    if (!joinedOutreach) {
        return { title, siblings: null, hiddenSiblingCount: 0 };
    }
    const shiftsById = new Map(
        joinedOutreach.shifts.map((sibling) => [sibling.id, sibling]),
    );
    const filledShiftIds = getFilledShiftIds(outreach.outreach_metadata);
    // Siblings absent from the joined payload (partial data) still count in
    // the title and the hidden tally; they just don't get a row.
    const knownSiblings = siblingIds
        .map((id) => shiftsById.get(id))
        .filter((sibling): sibling is Shift => sibling !== undefined)
        .sort((left, right) =>
            left.shift_start_time.localeCompare(right.shift_start_time),
        );
    const shownSiblings = knownSiblings.slice(0, MAX_GROUP_SIBLINGS_SHOWN);
    return {
        title,
        siblings: shownSiblings.map((sibling) => ({
            shiftId: sibling.id,
            label: `${formatDateWithWeekday(sibling.shift_start_time, timezone)} · ${formatCompactTime(sibling.shift_start_time, timezone)}–${formatCompactTime(sibling.shift_end_time, timezone)}`,
            ...resolveSiblingState(
                sibling,
                filledShiftIds,
                joinedOutreach.response_counts,
            ),
        })),
        hiddenSiblingCount: siblingIds.length - shownSiblings.length,
    };
}

function resolveSiblingState(
    sibling: Shift,
    filledShiftIds: ReadonlySet<string>,
    counts: JoinedOutreachRead['response_counts'] | undefined,
): { readonly stateLabel: string; readonly isPositive: boolean } {
    if (filledShiftIds.has(sibling.id)) {
        return { stateLabel: 'Filled', isPositive: true };
    }
    const accepted = getAcceptedCountForShift(counts, sibling.id);
    if (accepted > 0) {
        return { stateLabel: `${accepted} yes`, isPositive: true };
    }
    const bucket = getShiftCalendarStatusBucket(sibling);
    // An open sibling of an active fill outreach is being worked, not idle.
    if (bucket === 'open') {
        return { stateLabel: 'Outreaching', isPositive: false };
    }
    return {
        stateLabel: CALENDAR_CHIP_PALETTE[bucket].label,
        isPositive: false,
    };
}

function buildCardCore({
    shift,
    outreachStatus,
    joinedOutreach,
    timezone,
    now,
    unifiedStatus,
    formatName,
    displayLastFirst,
}: {
    readonly shift: Shift;
    readonly outreachStatus: ShiftOutreachStatus | undefined;
    readonly joinedOutreach: JoinedOutreachRead | undefined;
    readonly timezone: string;
    readonly now: Date;
    readonly unifiedStatus: UnifiedShiftStatus;
    readonly formatName: (fullName: string | null | undefined) => string;
    readonly displayLastFirst: boolean;
}): ShiftCardCore {
    // Precedence (first match wins). A missing/cancelled/completed/in-progress
    // shift and a clock-stage shift resolve here before any active outreach —
    // started-shift and EHR reality outrank outreach state.
    switch (unifiedStatus) {
        case 'missing':
            return missingCard();
        case 'cancelled':
            return cancelledCard(shift, now);
        case 'completed':
            return completedCard(shift, timezone);
        case 'in_progress':
            return inProgressCard(shift, timezone);
        case 'clock_in_reminder':
        case 'clock_out_reminder':
            return clockCard(shift, unifiedStatus, outreachStatus, now);
        case 'open':
        case 'not_scheduled':
        case 'assigned':
        case 'pending_confirmation':
        case 'confirmation_follow_up':
        case 'confirmed':
        case 'declined':
        case 'no_response':
        case 'needs_review':
        case 'needs_attention':
        case 'unknown': {
            const activeOutreach = outreachStatus?.activeFillOutreach;
            if (activeOutreach) {
                // EHR truth beats the running outreach: a shift that gained
                // its caregiver outside the outreach was filled elsewhere.
                if (isShiftFilledOutsideActiveOutreach(shift, activeOutreach)) {
                    return filledElsewhereCard(
                        shift,
                        activeOutreach,
                        formatName,
                    );
                }
                return outreachCard({
                    shift,
                    outreach: activeOutreach,
                    joinedOutreach,
                    timezone,
                    now,
                    formatName,
                    displayLastFirst,
                });
            }
            switch (unifiedStatus) {
                case 'open':
                    return openCard(shift, outreachStatus, now);
                case 'assigned':
                    return scheduledCard(shift, formatName);
                case 'pending_confirmation':
                case 'confirmation_follow_up':
                    return pendingConfirmationCard(shift, unifiedStatus, now);
                case 'confirmed':
                    return confirmedCard(shift, now);
                case 'declined':
                    return declinedCard(shift, formatName);
                case 'no_response':
                    return noResponseCard(shift);
                case 'needs_review':
                    return needsReviewReplyCard(shift);
                case 'not_scheduled':
                case 'needs_attention':
                case 'unknown':
                    return attentionExternalCard(shift, now);
                default:
                    return assert.never(unifiedStatus);
            }
        }
        default:
            return assert.never(unifiedStatus);
    }
}

function outreachCard({
    shift,
    outreach,
    joinedOutreach,
    timezone,
    now,
    formatName,
    displayLastFirst,
}: {
    readonly shift: Shift;
    readonly outreach: Outreach;
    readonly joinedOutreach: JoinedOutreachRead | undefined;
    readonly timezone: string;
    readonly now: Date;
    readonly formatName: (fullName: string | null | undefined) => string;
    readonly displayLastFirst: boolean;
}): ShiftCardCore {
    const metadata = outreach.outreach_metadata;
    const counts = joinedOutreach?.response_counts;
    // Per-shift yeses only: on a multi-shift outreach, a caregiver available
    // for a sibling shift must not make this shift's card claim a yes.
    const accepted = getAcceptedCountForShift(counts, shift.id);
    const needsReview = counts?.needs_review_count ?? 0;
    const agentRunning = joinedOutreach?.agent_is_actively_running ?? false;
    const startedAgo = formatDurationFromNow(outreach.created_at, now);
    // Group context (size, take-together, sibling states) lives in the
    // card-level group summary, so strip bodies stay status-only.
    const stripBase = {
        meta: { kind: 'text', label: `started ${startedAgo} ago` },
        body: [],
        showResponseSummary: true,
        urgencyLine: shiftStartedUrgencyLine(shift, now, 'still open'),
        showAgentRunningIndicator: agentRunning,
    } satisfies Omit<ShiftCardStrip, 'tone' | 'title'>;
    const filledShiftIds = getFilledShiftIds(metadata);

    // (a) Per-shift truth: this shift is filled while siblings keep outreaching.
    if (filledShiftIds.has(shift.id)) {
        const name = shift.caregiver?.name;
        return {
            kind: 'outreach_shift_filled',
            strip: {
                tone: 'active',
                title: name ? `Filled by ${formatName(name)}` : 'Filled',
                ...stripBase,
                urgencyLine: null,
            },
            primaryAction: outreachAction(outreach, 'View outreach'),
            secondaryAction: null,
        };
    }

    // (b) Inbound offer — lead with provenance, not progress counts.
    if (outreach.outreach_type === 'shift_discovery') {
        const name = joinedOutreach?.suggested_by_caregiver_name;
        const title = name
            ? `${formatName(name)} asked to pick up this shift`
            : 'A caregiver asked to pick up this shift';
        return {
            kind: 'outreach_discovery',
            strip: { tone: 'assign', title, ...stripBase },
            primaryAction: assignAction(outreach),
            secondaryAction: outreachAction(outreach, 'View outreach'),
        };
    }

    // (c) Someone said yes — flip to action-demanding, naming who when the
    // payload carries names ("Maria Garcia, Ana Lopez +1 said yes").
    if (
        accepted > 0 ||
        joinedOutreach?.display_status === 'awaiting_confirmation'
    ) {
        const acceptedNames = formatAcceptedCaregiverNames({
            counts,
            shiftId: shift.id,
            maxNames: 2,
            displayLastFirst,
        });
        const plural = accepted === 1 ? '' : 's';
        const title =
            acceptedNames !== null
                ? `${acceptedNames} said yes`
                : accepted > 0
                  ? `${accepted} caregiver${plural} said yes`
                  : 'A caregiver said yes';
        return {
            kind: 'outreach_accepted',
            strip: { tone: 'assign', title, ...stripBase },
            primaryAction: assignAction(outreach),
            secondaryAction: outreachAction(outreach, 'View outreach'),
        };
    }

    // (d) Ambiguous reply awaiting a human read.
    if (needsReview > 0) {
        const title =
            needsReview === 1
                ? '1 reply needs review'
                : `${needsReview} replies need review`;
        return {
            kind: 'outreach_needs_review',
            strip: { tone: 'review', title, ...stripBase },
            primaryAction: outreachAction(outreach, 'View outreach'),
            secondaryAction: null,
        };
    }

    // (e) Manually paused (status paused, or a paused scheduled outreach).
    if (
        outreach.status === 'paused' ||
        (outreach.status === 'pending' &&
            getScheduledOutreachState(metadata) === 'paused')
    ) {
        const pausedAt = getOutreachPausedAt(metadata);
        return {
            kind: 'outreach_paused',
            strip: {
                tone: 'warning',
                title: 'Paused',
                ...stripBase,
                meta: pausedAt
                    ? {
                          kind: 'text',
                          label: `${formatDurationFromNow(pausedAt, now)} ago`,
                      }
                    : stripBase.meta,
            },
            primaryAction: outreachAction(outreach, 'View outreach'),
            secondaryAction: null,
        };
    }

    // (f) Scheduled to start, not yet running.
    const scheduledStartAt = getScheduledOutreachStartAt(metadata);
    if (outreach.status === 'pending' && scheduledStartAt) {
        return {
            kind: 'outreach_scheduled',
            strip: {
                tone: 'active',
                title: `Starts outreach at ${formatTime(scheduledStartAt, timezone)}`,
                ...stripBase,
                meta: null,
            },
            primaryAction: outreachAction(outreach, 'View outreach'),
            secondaryAction: null,
        };
    }

    // (g) Working — a plain pending outreach was created but never kicked off.
    const isPending = outreach.status === 'pending';
    return {
        kind: 'outreach_working',
        strip: {
            tone: 'active',
            title: isPending ? 'Outreach hasn’t started yet' : 'Reaching out',
            ...stripBase,
            meta: {
                kind: 'text',
                label: isPending
                    ? `created ${startedAgo} ago`
                    : `started ${startedAgo} ago`,
            },
        },
        primaryAction: outreachAction(outreach, 'View outreach'),
        secondaryAction: null,
    };
}

// The shift gained its caregiver outside the still-running fill outreach
// (e.g. directly in the EHR) — surface the fill and point at the outreach so
// the coordinator can archive or redirect it.
function filledElsewhereCard(
    shift: Shift,
    outreach: Outreach,
    formatName: (fullName: string | null | undefined) => string,
): ShiftCardCore {
    const name = formatName(shift.caregiver?.name);
    return {
        kind: 'outreach_filled_elsewhere',
        strip: {
            tone: 'info',
            title: name ? `Filled elsewhere by ${name}` : 'Filled elsewhere',
            meta: null,
            body: [
                'Assigned outside this outreach while Phoebe was reaching out.',
            ],
            showResponseSummary: false,
            urgencyLine: null,
            showAgentRunningIndicator: false,
        },
        primaryAction: outreachAction(outreach, 'Review outreach'),
        secondaryAction: caregiverAction(shift, 'View caregiver'),
    };
}

// Filled urgency pill for future shifts that still need a human decision.
function startsInPill(shift: Shift, now: Date): ShiftCardStripMeta | null {
    if (isPastStart(shift, now)) {
        return null;
    }
    const duration = formatDurationFromNow(shift.shift_start_time, now);
    return { kind: 'pill', label: `Starts in ${duration}`, style: 'urgent' };
}

// Red sub-row shown once the shift has started and is still not settled.
function shiftStartedUrgencyLine(
    shift: Shift,
    now: Date,
    suffix: string,
): string | null {
    if (!isPastStart(shift, now)) {
        return null;
    }
    const duration = formatDurationFromNow(shift.shift_start_time, now);
    return `Shift started ${duration} ago, ${suffix}`;
}

export type TerminalOutreachArchiveReason =
    | 'filled_elsewhere'
    | 'no_longer_available'
    | 'other';

export type TerminalOutreachOutcome =
    | { readonly kind: 'filled'; readonly filledThisShift: boolean }
    | { readonly kind: 'unfilled' }
    | {
          readonly kind: 'archived';
          readonly reason: TerminalOutreachArchiveReason;
      };

/**
 * Outcome of a terminal fill outreach as it applies to one shift; null for
 * non-terminal statuses. A per-shift fill wins over the outreach-level
 * status, so on a multi-shift outreach a shift Phoebe filled reads as filled
 * even when the outreach was later archived for its remaining siblings.
 * Archive reasons come from the structured `removal_reason` stamped at
 * archive time; rows without it (older rows, system cancellations) resolve
 * to the generic 'other'.
 */
export function resolveTerminalOutreachOutcome(
    outreach: Outreach,
    shiftId: string,
): TerminalOutreachOutcome | null {
    switch (outreach.status) {
        case 'pending':
        case 'paused':
        case 'in_progress':
            return null;
        case 'filled':
        case 'unfilled':
        case 'cancelled': {
            if (getFilledShiftIds(outreach.outreach_metadata).has(shiftId)) {
                return { kind: 'filled', filledThisShift: true };
            }
            if (outreach.status === 'filled') {
                return { kind: 'filled', filledThisShift: false };
            }
            if (outreach.status === 'unfilled') {
                return { kind: 'unfilled' };
            }
            const removalReason = getOutreachRemovalReason(
                outreach.outreach_metadata,
            );
            // An outreach archived as "filled" that never recorded this
            // shift in filled_shifts was filled outside its own fill flow.
            const reason: TerminalOutreachArchiveReason =
                removalReason === 'filled-externally' ||
                removalReason === 'filled'
                    ? 'filled_elsewhere'
                    : removalReason === 'no_longer_available'
                      ? 'no_longer_available'
                      : 'other';
            return { kind: 'archived', reason };
        }
        default:
            return assert.never(outreach.status);
    }
}

/**
 * Extra outcome text for compact calendar cards. The normal status bucket
 * still controls color and filtering; this label adds the outreach outcome
 * that the EHR status alone cannot explain.
 */
export function getShiftChipOutcomeLabel(
    shift: Shift,
    outreachStatus: ShiftOutreachStatus | undefined,
): 'Filled elsewhere' | 'Archived' | null {
    const unifiedStatus = resolveShiftStatus(shift.status, shift.latest_stage);
    // Current, settled shift truth wins over historical outreach context.
    if (
        unifiedStatus === 'missing' ||
        unifiedStatus === 'cancelled' ||
        unifiedStatus === 'completed' ||
        unifiedStatus === 'in_progress' ||
        unifiedStatus === 'clock_in_reminder' ||
        unifiedStatus === 'clock_out_reminder'
    ) {
        return null;
    }

    const activeOutreach = outreachStatus?.activeFillOutreach;
    if (
        activeOutreach &&
        isShiftFilledOutsideActiveOutreach(shift, activeOutreach)
    ) {
        return 'Filled elsewhere';
    }

    const terminalOutreach = outreachStatus?.latestTerminalFillOutreach;
    if (!terminalOutreach) {
        return null;
    }
    const outcome = resolveTerminalOutreachOutcome(terminalOutreach, shift.id);
    if (outcome?.kind !== 'archived') {
        return null;
    }
    if (outcome.reason === 'filled_elsewhere') {
        return 'Filled elsewhere';
    }
    // An assigned-family shift has newer EHR truth than a generic archive.
    return SOURCE_ASSIGNED_UNIFIED_STATUSES.has(unifiedStatus)
        ? null
        : 'Archived';
}

const SOURCE_ASSIGNED_UNIFIED_STATUSES: ReadonlySet<UnifiedShiftStatus> =
    new Set([
        'assigned',
        'pending_confirmation',
        'confirmation_follow_up',
        'confirmed',
    ]);

// Popover body line describing how the last outreach on this shift ended.
function terminalOutreachLine(
    outreach: Outreach,
    shiftId: string,
    now: Date,
): string | null {
    const outcome = resolveTerminalOutreachOutcome(outreach, shiftId);
    if (outcome === null) {
        return null;
    }
    const endedAgo = formatDurationFromNow(
        getOutreachResolvedAt(outreach.outreach_metadata) ??
            outreach.created_at,
        now,
    );
    switch (outcome.kind) {
        case 'filled':
            return outcome.filledThisShift
                ? `Last outreach filled this shift ${endedAgo} ago`
                : `Last outreach ended ${endedAgo} ago`;
        case 'unfilled':
            return `Last outreach ended unfilled ${endedAgo} ago`;
        case 'archived':
            switch (outcome.reason) {
                case 'filled_elsewhere':
                    return `Outreach archived ${endedAgo} ago · marked filled elsewhere`;
                case 'no_longer_available':
                    return `Outreach archived ${endedAgo} ago · shift no longer available`;
                case 'other':
                    return `Outreach archived ${endedAgo} ago`;
                default:
                    return assert.never(outcome.reason);
            }
        default:
            return assert.never(outcome);
    }
}

function openCard(
    shift: Shift,
    outreachStatus: ShiftOutreachStatus | undefined,
    now: Date,
): ShiftCardCore {
    const roleLabel = shift.role_required
        ? getRoleDisplayName(shift.role_required)
        : 'caregiver';
    const terminal = outreachStatus?.latestTerminalFillOutreach;
    const terminalLine = terminal
        ? terminalOutreachLine(terminal, shift.id, now)
        : null;
    const body = terminalLine
        ? [terminalLine]
        : ['Phoebe hasn’t started outreach on this shift.'];
    return {
        kind: 'open',
        strip: {
            tone: 'critical',
            title: `No ${roleLabel} yet`,
            meta: startsInPill(shift, now),
            body,
            showResponseSummary: false,
            urgencyLine: shiftStartedUrgencyLine(shift, now, 'still open'),
            showAgentRunningIndicator: false,
        },
        primaryAction: {
            label: 'Find caregivers',
            cta: { kind: 'find_caregivers' },
        },
        secondaryAction: null,
    };
}

function scheduledCard(
    shift: Shift,
    formatName: (name: string | null | undefined) => string,
): ShiftCardCore {
    const skipped = shift.is_confirmation_skipped;
    const name = shift.caregiver?.name;
    return {
        kind: 'scheduled',
        strip: {
            tone: 'info',
            title: skipped ? 'Confirmations skipped' : 'Confirmation queued',
            meta: skipped
                ? null
                : { kind: 'pill', label: 'Auto', style: 'info' },
            body: [
                skipped
                    ? 'Phoebe won’t send a confirmation for this shift.'
                    : name
                      ? `Phoebe sends it to ${formatName(name)} automatically, nothing to do.`
                      : 'Phoebe sends the confirmation automatically, nothing to do.',
            ],
            showResponseSummary: false,
            urgencyLine: null,
            showAgentRunningIndicator: false,
        },
        primaryAction: caregiverAction(shift, 'View caregiver'),
        secondaryAction: {
            label: 'Find replacement',
            cta: { kind: 'find_caregivers' },
        },
    };
}

function pendingConfirmationCard(
    shift: Shift,
    unifiedStatus: 'pending_confirmation' | 'confirmation_follow_up',
    now: Date,
): ShiftCardCore {
    const ehrPendingConfirmation = isEhrPendingConfirmation(
        shift.status,
        shift.latest_stage,
    );
    const hasReply =
        !ehrPendingConfirmation && (shift.unread_message_count ?? 0) > 0;
    const statusChangedAt = ehrPendingConfirmation
        ? shift.external_snapshot_synced_at
        : shift.latest_stage_created_at;
    const title = ehrPendingConfirmation
        ? 'Pending confirmation in your EHR'
        : hasReply
          ? 'Caregiver replied'
          : unifiedStatus === 'confirmation_follow_up'
            ? 'Follow-up sent'
            : 'Confirmation sent';
    return {
        kind: 'pending_confirmation',
        strip: {
            tone: 'warning',
            title,
            meta:
                !hasReply && statusChangedAt
                    ? {
                          kind: 'text',
                          label: `${ehrPendingConfirmation ? 'synced' : 'sent'} ${formatDurationFromNow(
                              statusChangedAt,
                              now,
                          )} ago`,
                      }
                    : null,
            body: ehrPendingConfirmation
                ? ['Waiting for the caregiver to confirm.']
                : hasReply
                  ? []
                  : ['No reply yet.'],
            showResponseSummary: false,
            urgencyLine: shiftStartedUrgencyLine(
                shift,
                now,
                'still unconfirmed',
            ),
            showAgentRunningIndicator: false,
        },
        primaryAction: ehrPendingConfirmation
            ? { label: 'View details', cta: VIEW_DETAILS_CTA }
            : conversationAction(shift, 'View conversation'),
        secondaryAction: {
            label: 'Find replacement',
            cta: { kind: 'find_caregivers' },
        },
    };
}

function confirmedCard(shift: Shift, now: Date): ShiftCardCore {
    return {
        kind: 'confirmed',
        strip: quietStrip(
            'Confirmed',
            shift.latest_stage_created_at
                ? {
                      kind: 'text',
                      label: `${formatDurationFromNow(shift.latest_stage_created_at, now)} ago`,
                  }
                : null,
        ),
        primaryAction: caregiverAction(shift, 'View caregiver'),
        secondaryAction: null,
    };
}

function declinedCard(
    shift: Shift,
    formatName: (fullName: string | null | undefined) => string,
): ShiftCardCore {
    const name = formatName(shift.caregiver?.name) || 'Caregiver';
    return {
        kind: 'declined',
        strip: {
            tone: 'critical',
            title: `${name} declined`,
            meta: null,
            body: [],
            showResponseSummary: false,
            urgencyLine: null,
            showAgentRunningIndicator: false,
        },
        primaryAction: {
            label: 'Find replacement',
            cta: { kind: 'find_caregivers' },
        },
        secondaryAction: conversationAction(shift, 'View conversation'),
    };
}

function noResponseCard(shift: Shift): ShiftCardCore {
    return {
        kind: 'no_response',
        strip: {
            tone: 'critical',
            title: 'No reply to confirmation',
            meta: null,
            body: ['No-show risk.'],
            showResponseSummary: false,
            urgencyLine: null,
            showAgentRunningIndicator: false,
        },
        primaryAction: conversationAction(shift, 'View conversation'),
        secondaryAction: {
            label: 'Find replacement',
            cta: { kind: 'find_caregivers' },
        },
    };
}

function needsReviewReplyCard(shift: Shift): ShiftCardCore {
    return {
        kind: 'needs_review_reply',
        strip: {
            tone: 'review',
            title: 'Reply couldn’t be understood',
            meta: null,
            body: [],
            showResponseSummary: false,
            urgencyLine: null,
            showAgentRunningIndicator: false,
        },
        primaryAction: conversationAction(shift, 'View conversation'),
        secondaryAction: null,
    };
}

function attentionExternalCard(shift: Shift, now: Date): ShiftCardCore {
    return {
        kind: 'attention_external',
        strip: {
            tone: 'review',
            title: 'Check this shift in your EHR',
            meta: startsInPill(shift, now),
            body: shift.external_status ? [shift.external_status] : [],
            showResponseSummary: false,
            urgencyLine: null,
            showAgentRunningIndicator: false,
        },
        primaryAction: { label: 'View details', cta: VIEW_DETAILS_CTA },
        secondaryAction: null,
    };
}

function clockCard(
    shift: Shift,
    unifiedStatus: 'clock_in_reminder' | 'clock_out_reminder',
    outreachStatus: ShiftOutreachStatus | undefined,
    now: Date,
): ShiftCardCore {
    const isClockIn = unifiedStatus === 'clock_in_reminder';
    const clockOutreach = outreachStatus?.clockReminderOutreach;
    return {
        kind: 'clock_issue',
        strip: {
            tone: 'clock',
            title: isClockIn ? 'No clock-in' : 'No clock-out',
            meta: {
                kind: 'text',
                label: isClockIn
                    ? `started ${formatDurationFromNow(
                          shift.shift_start_time,
                          now,
                      )} ago`
                    : `ended ${formatDurationFromNow(
                          shift.shift_end_time,
                          now,
                      )} ago`,
            },
            body: [],
            showResponseSummary: false,
            urgencyLine: null,
            showAgentRunningIndicator: false,
        },
        primaryAction: clockOutreach
            ? {
                  label: 'Resolve clock time',
                  cta: { kind: 'view_outreach', outreachId: clockOutreach.id },
              }
            : { label: 'View details', cta: VIEW_DETAILS_CTA },
        secondaryAction: conversationAction(shift, 'View conversation'),
    };
}

function inProgressCard(shift: Shift, timezone: string): ShiftCardCore {
    const endTime = formatTime(shift.shift_end_time, timezone);
    const title =
        shift.latest_stage === 'clocked_in' && shift.latest_stage_created_at
            ? `Clocked in at ${formatTime(
                  shift.latest_stage_created_at,
                  timezone,
              )}`
            : 'In progress';
    return {
        kind: 'in_progress',
        strip: quietStrip(title, {
            kind: 'text',
            label: `ends at ${endTime}`,
        }),
        primaryAction: { label: 'View details', cta: VIEW_DETAILS_CTA },
        secondaryAction: caregiverAction(shift, 'View caregiver'),
    };
}

function completedCard(shift: Shift, timezone: string): ShiftCardCore {
    return {
        kind: 'completed',
        strip: quietStrip(
            'Completed',
            shift.latest_stage === 'clocked_out' &&
                shift.latest_stage_created_at
                ? {
                      kind: 'text',
                      label: `clocked out ${formatTime(
                          shift.latest_stage_created_at,
                          timezone,
                      )}`,
                  }
                : null,
        ),
        primaryAction: { label: 'View details', cta: VIEW_DETAILS_CTA },
        secondaryAction: null,
    };
}

function cancelledCard(shift: Shift, now: Date): ShiftCardCore {
    const body: Array<string> = [];
    if (shift.external_status) {
        let line = `${shift.external_status} in your EHR`;
        if (shift.external_snapshot_synced_at) {
            line += ` · synced ${formatDurationFromNow(
                shift.external_snapshot_synced_at,
                now,
            )} ago`;
        }
        body.push(line);
    }
    return {
        kind: 'cancelled',
        strip: quietStrip('Cancelled', null, body),
        primaryAction: { label: 'View details', cta: VIEW_DETAILS_CTA },
        secondaryAction: null,
    };
}

function missingCard(): ShiftCardCore {
    return {
        kind: 'missing',
        strip: quietStrip('No longer appears in your EHR sync'),
        primaryAction: { label: 'View details', cta: VIEW_DETAILS_CTA },
        secondaryAction: null,
    };
}

function quietStrip(
    title: string,
    meta: ShiftCardStripMeta | null = null,
    body: Array<string> = [],
): ShiftCardStrip {
    return {
        tone: 'quiet',
        title,
        meta,
        body,
        showResponseSummary: false,
        urgencyLine: null,
        showAgentRunningIndicator: false,
    };
}

function assignAction(outreach: Outreach): ShiftCardAction {
    return {
        label: 'Review & assign',
        cta: {
            kind: 'view_outreach',
            outreachId: outreach.id,
            outreachAction: 'assign_and_fill',
        },
    };
}

function outreachAction(outreach: Outreach, label: string): ShiftCardAction {
    return { label, cta: { kind: 'view_outreach', outreachId: outreach.id } };
}

function caregiverAction(shift: Shift, label: string): ShiftCardAction {
    const caregiverId = shift.assigned_caregiver_id ?? shift.caregiver?.id;
    return caregiverId
        ? { label, cta: { kind: 'view_caregiver', caregiverId } }
        : { label: 'View details', cta: VIEW_DETAILS_CTA };
}

function conversationAction(shift: Shift, label: string): ShiftCardAction {
    const conversationId = shift.conversation_id;
    return conversationId
        ? { label, cta: { kind: 'view_conversation', conversationId } }
        : { label: 'View details', cta: VIEW_DETAILS_CTA };
}

function isPastStart(shift: Shift, now: Date): boolean {
    return new Date(shift.shift_start_time).getTime() <= now.getTime();
}

function buildUnreadBadge(shift: Shift): ShiftCardUnreadBadge | null {
    const count = shift.unread_message_count ?? 0;
    const conversationId = shift.conversation_id;
    return count > 0 && conversationId ? { count, conversationId } : null;
}

function finalizeCard(
    core: ShiftCardCore,
    unreadBadge: ShiftCardUnreadBadge | null,
    group: ShiftCardGroup | null,
): ShiftCardState {
    const isViewDetails = (action: ShiftCardAction | null): boolean =>
        action?.cta.kind === 'view_details';
    // Drop a fallen-back secondary that duplicates a view-details primary, and
    // suppress the ghost when a view-details action is already shown.
    const secondaryAction =
        core.secondaryAction &&
        isViewDetails(core.secondaryAction) &&
        isViewDetails(core.primaryAction)
            ? null
            : core.secondaryAction;
    const ghostActions =
        isViewDetails(core.primaryAction) || isViewDetails(secondaryAction)
            ? []
            : [{ label: 'View details', cta: VIEW_DETAILS_CTA }];
    return {
        kind: core.kind,
        strip: core.strip,
        primaryAction: core.primaryAction,
        secondaryAction,
        ghostActions,
        unreadBadge,
        group,
    };
}
